const formatRad = (coordinate) => (coordinate * Math.PI) / 180;

/**
 * @description Takes two locations and returns the distance between them in km.
 *
 * @param {Array<number>}  location1  An array of size two containing a latitude (first element) and
 *                                    longitude (second element)
 *
 * @param {Array<number>}  location2  An array of size two containing a latitude (first element) and
 *                                    longitude (second element)
 *
 * @return {string} The distance between the two locations in km.
 * Returns null if two locations are not provided.
 */
export const distanceBetweenCoordinates = (location1, location2) => {
  if (!location1 || !location2) return null;
  const location1LatRad = formatRad(location1[0]);
  const location1LngRad = formatRad(location1[1]);
  const location2LatRad = formatRad(location2[0]);
  const location2LngRad = formatRad(location2[1]);

  const distance = Math.round(
    6377.830272 *
      Math.acos(
        Math.sin(location1LatRad) * Math.sin(location2LatRad) +
          Math.cos(location1LatRad) *
            Math.cos(location2LatRad) *
            Math.cos(location2LngRad - location1LngRad),
      ),
  );

  return `${distance}km`;
};

/**
 * @description Takes a point and a distance and creates a square around that point of size distance
 * If the distance is 100km, it will be a ~200km square in diameter - 100km in each direction
 * around the point.
 *
 * Some of the math in this function was used from https://stackoverflow.com/questions/10398481/given-a-lat-lng-coordinate-calculate-the-min-and-max-lat-lng-values-for-a-10-km
 *
 * @param {number}  lat  latitude of point
 *
 * @param {number}  lng  longitude of point
 *
 * @param {number} distance distance in km the box should be around the point
 *
 * @return {Object} The lat long ranges of the bounding box
 */
export const boundingBoxAroundPoint = (lat, lng, distance = 100) => {
  if (!lat || Number.isNaN(Number(lat)) || !lng || Number.isNaN(Number(lng))) {
    return undefined;
  }
  const kilometerInDeg = 0.0089982311916;
  const distanceInDeg = kilometerInDeg * distance;
  return {
    lat: [Number(lat) - distanceInDeg, Number(lat) + distanceInDeg],
    lng: [
      Number(lng) - distanceInDeg / Math.cos((lat * Math.PI) / 180),
      Number(lng) + distanceInDeg / Math.cos((lat * Math.PI) / 180),
    ],
  };
};
